import { Badge, Box, Card, Flex, Grid, Heading, ProviderIcon, Text } from '@workos-inc/design-system';
import { getMatter } from '@workos-inc/docs-kit';
import { glob } from 'glob';
import Head from 'next/head';
import NextLink from 'next/link';
import { GetStaticProps } from 'next/types';
import path from 'path';
import { Header } from 'src/components/header';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { Subtitle } from 'src/components/subtitle';
import { CONTENT_DIR } from 'src/utils/constants';
import { createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
interface Migration {
  url: string;
  title: string;
  description: string;
  icon?: React.ComponentPropsWithoutRef<typeof ProviderIcon>['provider'];
  badge?: string;
}
interface MigratePageProps {
  standaloneApiMigrations: Migration[];
  serviceMigrations: Migration[];
  pageMeta: {
    title: string;
    canonicalUrl: string;
    description: string;
    image: string;
  };
}
export const getStaticProps: GetStaticProps<MigratePageProps> = async () => {
  const files = await glob(`${CONTENT_DIR}/migrate/[!_]?*.mdx`);
  const filesMeta = files.map<Migration>(file => {
    const data = getMatter(file);
    const url = '/migrate/' + path.basename(file).replace('.mdx', '');
    // Introducing lint rule banning type assertions
    // This type assertion assumes data contains a title and description
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const meta = {
      url,
      ...data
    } as Migration;
    return meta;
  });
  const standaloneApiUrlsWithTitles = [['Single Sign-On', '/migrate/standalone-sso']];
  const standaloneApiMigrations = standaloneApiUrlsWithTitles.map(([title, url]) => {
    const migration = filesMeta.find(file => file.url === url);
    return migration ? {
      ...migration,
      title
    } : null;
  }).filter((migration): migration is Migration => !!migration);
  const serviceMigrations = filesMeta.filter(({
    url
  }) => !standaloneApiUrlsWithTitles.map(([, guideUrl]) => guideUrl).includes(url)).sort((a, b) => a.title === 'Migrate from other services' ? 1 : a.title.localeCompare(b.title)).map(data => {
    let title = data.title.replace(/^Migrate from /, '');
    title = title.charAt(0).toUpperCase() + title.slice(1);
    return {
      ...data,
      title
    };
  });
  const title = 'Migrations';
  const description = 'Guides on migrating to WorkOS from an internal or third-party solution.';
  const image = await createPageImage('Migrate', description);
  return {
    props: {
      standaloneApiMigrations,
      serviceMigrations,
      pageMeta: {
        title,
        canonicalUrl: 'https://workos.com/docs/migrate',
        description,
        image
      }
    }
  };
};
const MigratePage = ({
  serviceMigrations,
  standaloneApiMigrations,
  pageMeta
}: MigratePageProps) => {
  const title = createPageTitle(pageMeta.title);
  const {
    description,
    image
  } = pageMeta;
  return <Box position="relative" data-sentry-element="Box" data-sentry-component="MigratePage" data-sentry-source-file="migrate.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="migrate.tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="migrate.tsx" />
        <link href={pageMeta.canonicalUrl} rel="canonical" />
      </Head>

      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="migrate.tsx" />

      <Header caption="Migrations" data-sentry-element="Header" data-sentry-source-file="migrate.tsx" />

      <PageLayout.Root layout="index" data-sentry-element="PageLayout.Root" data-sentry-source-file="migrate.tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="migrate.tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="migrate.tsx" />
        </PageLayout.Nav>

        <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="migrate.tsx">
          <Box mb={{
          initial: '7',
          sm: '9'
        }} data-sentry-element="Box" data-sentry-source-file="migrate.tsx">
            <Heading as="h1" mb="2" size={{
            initial: '7',
            sm: '8'
          }} data-sentry-element="Heading" data-sentry-source-file="migrate.tsx">
              Migrate to WorkOS
            </Heading>
            <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="migrate.tsx">{description}</Subtitle>
          </Box>

          <Box mb={{
          initial: '7',
          sm: '9'
        }} data-sentry-element="Box" data-sentry-source-file="migrate.tsx">
            <Heading as="h2" mb="5" size="5" data-sentry-element="Heading" data-sentry-source-file="migrate.tsx">
              Migrate users from another service
            </Heading>

            <Grid columns={{
            sm: '2',
            md: '3'
          }} gap="4" mb="8" data-sentry-element="Grid" data-sentry-source-file="migrate.tsx">
              {serviceMigrations.map(migration => <MigrationCard key={migration.url} migration={migration} />)}
            </Grid>
          </Box>

          <Heading as="h2" mb="5" size="5" data-sentry-element="Heading" data-sentry-source-file="migrate.tsx">
            Migrate users from a WorkOS API
          </Heading>

          <Box mb="6" data-sentry-element="Box" data-sentry-source-file="migrate.tsx">
            <Grid columns={{
            sm: '2',
            md: '3'
          }} gap="4" mb="4" data-sentry-element="Grid" data-sentry-source-file="migrate.tsx">
              {standaloneApiMigrations.map(migration => <MigrationCard key={migration.url} migration={migration} />)}
            </Grid>
          </Box>
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};
const MigrationCard = ({
  migration
}: {
  migration: Migration;
}) => {
  const {
    url,
    title,
    badge,
    description,
    icon
  } = migration;
  return <Card key={url} asChild size={{
    initial: '3',
    md: '4'
  }} variant="classic" data-sentry-element="Card" data-sentry-component="MigrationCard" data-sentry-source-file="migrate.tsx">
      <NextLink href={url} data-sentry-element="NextLink" data-sentry-source-file="migrate.tsx">
        <Flex align="center" gap="4" justify="between" my="-1" data-sentry-element="Flex" data-sentry-source-file="migrate.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="migrate.tsx">
            <Flex align="center" gap="2" mb="1" data-sentry-element="Flex" data-sentry-source-file="migrate.tsx">
              <Heading as="h3" size="3" trim="start" data-sentry-element="Heading" data-sentry-source-file="migrate.tsx">
                {title}
              </Heading>
              {badge && <Badge color="purple">{badge}</Badge>}
            </Flex>
            <Text as="div" color="gray" size="2" trim="end" wrap="balance" data-sentry-element="Text" data-sentry-source-file="migrate.tsx">
              {description}
            </Text>
          </Box>

          {icon && <ProviderIcon mr="-2" provider={icon} />}
        </Flex>
      </NextLink>
    </Card>;
};
export default MigratePage;